import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import { MessageCircle, Reply, Trash2, Send, Pencil, Heart, HandHeart, Lock } from "lucide-react";

const EDIT_WINDOW_MS = 60 * 60 * 1000; // Keep in lockstep with COMMENT_EDIT_WINDOW_SECONDS on the backend.

// Ordered pair — the UI renders each reaction inline in this order.
const REACTIONS = [
  { type: "heart", label: "Love", icon: Heart, color: "var(--terracotta)" },
  { type: "amen",  label: "Amen", icon: HandHeart, color: "var(--gold)" },
];

function fmtWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return iso.slice(0, 10); }
}

function initials(name) {
  const parts = (name || "").trim().split(/\s+/);
  if (parts.length === 0) return "?";
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase() || "?";
}

function ReactionBar({ c, currentUser, onToggle, canWrite }) {
  if (c.deleted) return null;
  const my = new Set(c.my_reactions || []);
  return (
    <div className="flex items-center gap-1.5" data-testid={`journal-reactions-${c.id}`}>
      {REACTIONS.map(({ type, label, icon: Icon, color }) => {
        const count = c.reactions?.[type] || 0;
        const active = my.has(type);
        const disabled = !canWrite;
        return (
          <button
            key={type}
            onClick={() => canWrite && onToggle(c, type)}
            disabled={disabled}
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[0.65rem] transition-colors"
            style={{
              background: active ? "var(--bg-alt)" : "transparent",
              border: `1px solid ${active ? color : "var(--border-subtle)"}`,
              color: active ? color : "var(--muted)",
              cursor: disabled ? "not-allowed" : "pointer",
              opacity: disabled ? 0.6 : 1,
            }}
            title={disabled ? (currentUser ? "Members only — become a member to react" : "Sign in as a member to react") : (active ? `Remove ${label}` : label)}
            data-testid={`journal-react-${type}-${c.id}`}
            aria-pressed={active}
            aria-label={`${label} (${count})`}
          >
            <Icon size={11} fill={active ? color : "none"} />
            {count > 0 && <span>{count}</span>}
          </button>
        );
      })}
    </div>
  );
}

function CommentCard({ c, currentUser, canWrite, onReply, onDelete, onEdit, onReact, isReply = false }) {
  const isMine = currentUser && currentUser.id === c.user_id;
  const canDelete = currentUser && (isMine || currentUser.is_admin) && !c.deleted;
  // 1-hour edit window from creation. Admins bypass the window; authors get
  // it disabled once the comment "sets".
  const withinWindow = useMemo(() => {
    if (!c.created_at) return false;
    try { return Date.now() - new Date(c.created_at).getTime() < EDIT_WINDOW_MS; }
    catch { return false; }
  }, [c.created_at]);
  const canEdit = currentUser && !c.deleted && canWrite && (currentUser.is_admin || (isMine && withinWindow));
  return (
    <div
      className="flex gap-3"
      style={isReply ? { marginLeft: "2.5rem" } : undefined}
      data-testid={`journal-comment-${c.id}`}
    >
      <div
        className="shrink-0 rounded-full flex items-center justify-center font-serif-display"
        style={{
          width: 34, height: 34,
          background: c.deleted ? "var(--surface-hover)" : "var(--bg-alt)",
          color: c.deleted ? "var(--muted)" : "var(--ember)",
          border: "1px solid var(--border-subtle)",
          fontSize: "0.75rem",
        }}
      >
        {c.deleted ? "—" : initials(c.author_name)}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-sm" style={{ color: c.deleted ? "var(--muted)" : "var(--ink)", fontWeight: 500 }}>
            {c.deleted ? "Removed" : c.author_name}
          </span>
          <span className="text-[0.65rem]" style={{ color: "var(--muted)" }}>{fmtWhen(c.created_at)}</span>
          {c.edited_at && !c.deleted && (
            <span className="text-[0.65rem] italic" style={{ color: "var(--muted)" }} title={`Edited ${fmtWhen(c.edited_at)}`}>· edited</span>
          )}
        </div>
        <p className="mt-1 text-sm whitespace-pre-line" style={{ color: c.deleted ? "var(--muted)" : "var(--ink-2)", fontStyle: c.deleted ? "italic" : "normal" }}>
          {c.body}
        </p>
        {!c.deleted && (
          <div className="mt-1.5 flex items-center gap-3 text-[0.7rem] flex-wrap" style={{ color: "var(--muted)" }}>
            <ReactionBar c={c} currentUser={currentUser} onToggle={onReact} canWrite={canWrite} />
            {!isReply && canWrite && (
              <button onClick={() => onReply(c)} className="inline-flex items-center gap-1 hover:opacity-80" data-testid={`journal-reply-btn-${c.id}`}>
                <Reply size={11} /> Reply
              </button>
            )}
            {canEdit && (
              <button onClick={() => onEdit(c)} className="inline-flex items-center gap-1 hover:opacity-80" data-testid={`journal-edit-btn-${c.id}`}>
                <Pencil size={11} /> Edit
              </button>
            )}
            {canDelete && (
              <button onClick={() => onDelete(c)} className="inline-flex items-center gap-1 hover:opacity-80" style={{ color: "var(--terracotta)" }} data-testid={`journal-delete-btn-${c.id}`}>
                <Trash2 size={11} /> Delete
              </button>
            )}
            {isMine && !canEdit && !c.deleted && (
              <span className="italic" title="This comment is more than an hour old — editing has closed. You can still delete.">Editing closed</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ComposeBox({ placeholder, onSubmit, onCancel, submitLabel = "Post", autoFocus = false, testidPrefix = "journal-compose", initialValue = "" }) {
  const [value, setValue] = useState(initialValue);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const body = value.trim();
    if (!body) return;
    setBusy(true);
    try {
      await onSubmit(body);
      if (!initialValue) setValue("");
    } finally { setBusy(false); }
  };
  return (
    <div className="rounded-md p-3" style={{ background: "var(--surface)", border: "1px solid var(--border-subtle)" }} data-testid={testidPrefix}>
      <textarea
        rows={3}
        value={value}
        placeholder={placeholder}
        onChange={(e) => setValue(e.target.value.slice(0, 4000))}
        className="w-full text-sm px-3 py-2 rounded"
        autoFocus={autoFocus}
        data-testid={`${testidPrefix}-input`}
      />
      <div className="mt-2 flex items-center justify-between">
        <span className="text-[0.65rem]" style={{ color: "var(--muted)" }}>{value.length}/4000</span>
        <div className="flex items-center gap-2">
          {onCancel && (
            <button onClick={onCancel} className="btn-ghost text-xs" data-testid={`${testidPrefix}-cancel`}>Cancel</button>
          )}
          <button onClick={submit} disabled={busy || !value.trim()} className="btn-primary text-xs" data-testid={`${testidPrefix}-submit`}>
            <Send size={11} /> {busy ? "…" : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function JournalComments({ postId }) {
  const { user } = useAuth();
  const canWrite = !!(user && user.membership_active);
  const [threads, setThreads] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [replyingTo, setReplyingTo] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const load = () => {
    setLoading(true);
    api.get(`/blog/${postId}/comments`)
      .then((r) => { setThreads(r.data.comments || []); setCount(r.data.count || 0); })
      .catch(() => { setThreads([]); setCount(0); })
      .finally(() => setLoading(false));
  };
  useEffect(load, [postId]);

  const post = async (body, parent_id = null) => {
    try {
      await api.post(`/blog/${postId}/comments`, { body, parent_id });
      setReplyingTo(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't post comment.");
      throw err;
    }
  };

  const remove = async (c) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/blog/comments/${c.id}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't delete comment.");
    }
  };

  const saveEdit = async (c, body) => {
    try {
      await api.put(`/blog/comments/${c.id}`, { body });
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update comment.");
      throw err;
    }
  };

  const findCommentById = (id) => {
    for (const t of threads) {
      if (t.id === id) return t;
      for (const r of t.replies || []) if (r.id === id) return r;
    }
    return null;
  };

  const react = async (c, type) => {
    // Optimistic toggle — flip locally, then reconcile from server.
    setThreads((prev) => prev.map((t) => {
      const bump = (row) => {
        if (row.id !== c.id) return row;
        const my = new Set(row.my_reactions || []);
        const counts = { ...(row.reactions || {}) };
        const wasActive = my.has(type);
        if (wasActive) { my.delete(type); counts[type] = Math.max(0, (counts[type] || 0) - 1); }
        else           { my.add(type);    counts[type] = (counts[type] || 0) + 1; }
        return { ...row, my_reactions: [...my], reactions: counts };
      };
      const top = bump(t);
      const replies = (t.replies || []).map(bump);
      return { ...top, replies };
    }));
    try {
      await api.post(`/blog/comments/${c.id}/react`, { type });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save reaction.");
      load(); // roll back to server truth
    }
  };

  const visibleCount = useMemo(
    () => threads.reduce((s, t) => s + 1 + (t.replies?.length || 0), 0),
    [threads],
  );

  return (
    <section className="mt-16 max-w-2xl" data-testid="journal-comments">
      <div className="editorial-rule mb-8" />
      <div className="flex items-center gap-2 mb-6">
        <MessageCircle size={16} style={{ color: "var(--ember)" }} />
        <h2 className="font-serif-display text-2xl" style={{ color: "var(--ink)" }}>
          Conversation
        </h2>
        <span className="text-xs" style={{ color: "var(--muted)" }}>· {count} comment{count === 1 ? "" : "s"}</span>
      </div>

      {user && user.membership_active ? (
        <div className="mb-8">
          <ComposeBox
            placeholder="Share what this stirred in you…"
            onSubmit={(body) => post(body)}
            submitLabel="Post comment"
          />
        </div>
      ) : user ? (
        <div
          className="mb-8 p-5 rounded-md"
          style={{ background: "var(--surface-hover)", border: "1px solid var(--gold)" }}
          data-testid="journal-comments-membership-cta"
        >
          <div className="flex items-center gap-2 mb-1">
            <Lock size={13} style={{ color: "var(--gold)" }} />
            <span className="overline" style={{ color: "var(--gold)" }}>Members-only conversation</span>
          </div>
          <p className="text-sm" style={{ color: "var(--ink-2)" }}>
            You can read everything in the thread below, but posting and replying are reserved for
            members of The Disciple Life. Join to add your voice — it also gets you the monthly
            letter and early-access chapters.
          </p>
          <Link
            to="/membership"
            data-testid="journal-comments-become-member"
            className="btn-primary mt-3 inline-flex"
          >
            Become a member — $4.99/mo
          </Link>
        </div>
      ) : (
        <div
          className="mb-8 p-5 rounded-md"
          style={{ background: "var(--surface-hover)", border: "1px solid var(--gold)" }}
          data-testid="journal-comments-signin"
        >
          <div className="flex items-center gap-2 mb-1">
            <Lock size={13} style={{ color: "var(--gold)" }} />
            <span className="overline" style={{ color: "var(--gold)" }}>Join the conversation</span>
          </div>
          <p className="text-sm" style={{ color: "var(--ink-2)" }}>
            Reading is on us. Commenting is for members — <Link to={`/auth?next=/blog/${postId}`} style={{ color: "var(--ember)" }} className="underline">sign in</Link> if you already are,
            or become a member below to add your voice.
          </p>
          <Link
            to="/membership"
            data-testid="journal-comments-become-member"
            className="btn-primary mt-3 inline-flex"
          >
            Become a member — $4.99/mo
          </Link>
        </div>
      )}

      {loading ? (
        <p className="text-sm" style={{ color: "var(--muted)" }}>Loading conversation…</p>
      ) : visibleCount === 0 ? (
        <p className="text-sm italic" style={{ color: "var(--muted)" }} data-testid="journal-comments-empty">
          No comments yet — be the first to reflect on this piece.
        </p>
      ) : (
        <ul className="space-y-6" data-testid="journal-comments-list">
          {threads.map((c) => (
            <li key={c.id} className="space-y-4">
              {editingId === c.id ? (
                <ComposeBox
                  placeholder="Edit your comment…"
                  initialValue={c.body}
                  onSubmit={(body) => saveEdit(c, body)}
                  onCancel={() => setEditingId(null)}
                  submitLabel="Save"
                  autoFocus
                  testidPrefix={`journal-edit-${c.id}`}
                />
              ) : (
                <CommentCard c={c} currentUser={user} canWrite={canWrite} onReply={() => setReplyingTo(c.id)} onDelete={remove} onEdit={(x) => setEditingId(x.id)} onReact={react} />
              )}
              {replyingTo === c.id && canWrite && (
                <div style={{ marginLeft: "2.5rem" }}>
                  <ComposeBox
                    placeholder={`Reply to ${c.author_name}…`}
                    onSubmit={(body) => post(body, c.id)}
                    onCancel={() => setReplyingTo(null)}
                    submitLabel="Post reply"
                    autoFocus
                    testidPrefix={`journal-reply-${c.id}`}
                  />
                </div>
              )}
              {(c.replies || []).map((r) => (
                editingId === r.id ? (
                  <div key={r.id} style={{ marginLeft: "2.5rem" }}>
                    <ComposeBox
                      placeholder="Edit your reply…"
                      initialValue={r.body}
                      onSubmit={(body) => saveEdit(r, body)}
                      onCancel={() => setEditingId(null)}
                      submitLabel="Save"
                      autoFocus
                      testidPrefix={`journal-edit-${r.id}`}
                    />
                  </div>
                ) : (
                  <CommentCard key={r.id} c={r} currentUser={user} canWrite={canWrite} onReply={() => setReplyingTo(c.id)} onDelete={remove} onEdit={(x) => setEditingId(x.id)} onReact={react} isReply />
                )
              ))}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
